import React, { useEffect } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./ProgramsBanner.css";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
 
// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};
 
const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};
 
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};
 
// Renamed component
const Water = () => {
  // Data for the initiatives section for cleaner code
  const initiatives = [
    "Diversion-Based & Canal Irrigation",
    "Check Dams & Percolation Tanks",
    "Farm Ponds, Wells & Micro-Irrigation",
    "Tube Well Installation in Cyclone Areas",
    "Spring-Based Drinking Water Supply",
    "Water Quality Monitoring & Training",
  ];
 
  const { openDonation } = useDonation();
  const navigate = useNavigate();
 
  useEffect(() => {
    const els = document.querySelectorAll('.wa-animate');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('in-view');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.16 });
    els.forEach((el) => io.observe(el));
 
    const counters = document.querySelectorAll('.waA-kpi .num');
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = +el.getAttribute('data-target') || 0;
        const duration = 1400;
        const start = performance.now();
        function step(ts) {
          const t = Math.min(1, (ts - start) / duration);
          const eased = t * (2 - t);
          el.textContent = Math.floor(target * eased);
          if (t < 1) requestAnimationFrame(step);
          else el.textContent = target + (target > 999 ? '+' : '');
        }
        requestAnimationFrame(step);
        counterObserver.unobserve(el);
      });
    }, { threshold: 0.6 });
    counters.forEach((c) => counterObserver.observe(c));
 
    return () => { io.disconnect(); counterObserver.disconnect(); };
  }, []);
 
  return (
    <>
      <Header />
 
      {/* Hero Section */}
      <section className="page-header">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/water.png)",
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Water
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Vikash supports water initiatives by improving access to clean water, promoting conservation, and ensuring sustainable water management for communities.
            </motion.p>
          </div>
        </Container>
      </section>
 
      {/* Water Program Section (REFRESHED UI) */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&display=swap');
        :root{--waA:#0f7a3a;--waB:#1f8f4d;--muted:#eaf7ef;--r:22px;--maxW:1200px}
        .waA-wrap{max-width:var(--maxW);margin:0 auto;padding:0 18px}
        .waA-section{padding:88px 0}
        .waA-topwave{height:60px;overflow:hidden}
        .waA-topwave svg{width:100%;height:100%}
        .waA-intro{display:grid;grid-template-columns:1.05fr .95fr;gap:32px;align-items:center;margin-bottom:52px}
        .waA-badge{display:inline-flex;align-items:center;gap:10px;padding:10px 18px;border-radius:999px;background:linear-gradient(90deg,var(--waB),var(--waA));color:#fff;font-weight:700;font-size:.95rem;letter-spacing:.01em}
        .waA-title{font-family:Poppins, sans-serif;font-size:3rem;font-weight:800;color:var(--waA);margin:20px 0 22px;line-height:1.03}
        .waA-copy{max-width:700px;color:#1e4336;font-size:1.05rem;line-height:1.9}
        .waA-highlights{display:grid;grid-template-columns:repeat(2,minmax(160px,1fr));gap:16px;margin-top:28px}
        .waA-chip{padding:16px 18px;background:rgba(234,247,239,0.9);border-radius:20px;color:var(--waA);font-weight:700;box-shadow:0 14px 34px rgba(15,122,58,0.08)}
        .waA-media{position:relative;display:flex;justify-content:center;align-items:center}
        .waA-media::before{content:'';position:absolute;bottom:-12px;left:-12px;width:90%;height:84%;border-radius:34px;background:rgba(234,247,239,0.75);z-index:0;transform:rotate(-2deg)}
        .waA-frame{position:relative;z-index:1;border-radius:34px;overflow:hidden;box-shadow:0 28px 88px rgba(12,80,55,0.14);border:1px solid rgba(255,255,255,0.85)}
        .waA-frame img{width:100%;height:auto;display:block;max-width:560px}
        .waA-programs{background:rgba(255,255,255,0.98);padding:36px 30px 30px;border-radius:30px;box-shadow:0 28px 78px rgba(12,80,55,0.08)}
        .waA-programs .hdr{text-align:center;margin-bottom:28px}
        .waA-programs h3{font-size:2rem;color:#0f7a3a;margin-bottom:12px}
        .waA-programs p{color:#1f4782;font-size:1rem;line-height:1.8}
        .waA-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:20px}
        .waA-card{background:linear-gradient(180deg,#fff,var(--muted));border-radius:26px;padding:26px 28px;display:grid;grid-template-columns:92px 1fr;gap:18px;align-items:center;box-shadow:0 18px 46px rgba(15,122,58,0.06);transition:transform .28s ease,box-shadow .28s ease}
        .waA-card:hover{transform:translateY(-6px);box-shadow:0 26px 66px rgba(15,122,58,0.12)}
        .waA-icon{width:92px;height:92px;border-radius:50%;background:linear-gradient(135deg,var(--waB),var(--waA));display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:18px}
        .waA-card h5{margin:0;color:#0f4d50;font-weight:700;font-size:1.05rem}
        .waA-card p{margin:8px 0 0;color:#2b5657;font-size:0.97rem;line-height:1.7}
        .waA-impact{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:18px;margin:42px 0}
        .waA-kpi{background:#fff;padding:22px 20px;border-radius:22px;box-shadow:0 14px 40px rgba(15,122,58,0.06);text-align:center}
        .waA-kpi .num{font-size:2rem;font-weight:800;color:var(--waA);margin-bottom:8px}
        .waA-kpi div:last-child{color:#2c5344;line-height:1.6;font-size:.97rem}
        .waA-cta{background:linear-gradient(135deg,var(--waB),var(--waA));border-radius:30px;padding:48px 30px;color:#fff;text-align:center;margin-top:18px;box-shadow:0 28px 90px rgba(15,122,58,0.16)}
        .waA-cta h2{font-size:2.35rem;margin-bottom:14px;font-weight:800}
        .waA-cta p{max-width:680px;margin:0 auto 26px;color:rgba(255,255,255,0.92);font-size:1.02rem;line-height:1.8}
        .waA-cta .btn-donate{background:#fff;color:var(--waA);border:2px solid #fff;font-weight:700;padding:.95rem 2rem}
        .waA-cta .btn-join{background:rgba(255,255,255,0.18);border:1px solid rgba(255,255,255,0.35);color:#fff;font-weight:700;padding:.95rem 2rem}
        @media(max-width:1024px){.waA-intro{grid-template-columns:1fr}.waA-grid{grid-template-columns:1fr}.waA-impact{grid-template-columns:repeat(2,minmax(0,1fr))}}
        @media(max-width:640px){.waA-topwave{height:32px}.waA-badge{font-size:.9rem;padding:8px 14px}.waA-title{font-size:2.2rem}.waA-highlights{grid-template-columns:1fr}.waA-card{grid-template-columns:72px 1fr}.waA-img{width:100%}.waA-cta h2{font-size:1.85rem}}
      `}</style>
 
      <section className="waA-section" aria-labelledby="waA-about-heading">
        <div className="waA-wrap">
          <div className="waA-topwave" aria-hidden="true">
            <svg viewBox="0 0 1200 60" preserveAspectRatio="none"><path d="M0,28 C250,80 450,0 600,24 C750,48 950,10 1200,28 L1200 0 L0 0 Z" fill="#eefaff" /></svg>
          </div>
 
          <div className="waA-intro wa-animate">
            <div>
              <span className="waA-badge">Sustainable Development Since 1987</span>
              <h2 className="waA-title" id="waA-about-heading">Securing a Vital Resource</h2>
              <p className="waA-copy">
                Water is fundamental to ensuring food security and sustainable livelihoods. Vikash has implemented extensive watershed and agriculture development programs across multiple districts, including Koraput, Boudh, Bargarh, Puri, and Kalahandi. Our goal is to optimize the utilization of precious water resources for both consumption and agriculture.
              </p>
              <div className="waA-highlights">
                <div className="waA-chip">River basin regeneration</div>
                <div className="waA-chip">Community water stewardship</div>
                <div className="waA-chip">Climate-resilient infrastructure</div>
                <div className="waA-chip">Sustainable irrigation design</div>
              </div>
            </div>
 
            <div className="waA-media" role="img" aria-label="Water management project image">
              <div className="waA-frame">
                <img src="/assets/images/Subsections/w1.jpg" alt="Water management project" />
              </div>
            </div>
          </div>
 
          <div className="waA-programs wa-animate" role="region" aria-label="Our Water Management Solutions">
            <div className="hdr">
              <h3 className="section-header">Our Water Management Solutions</h3>
              <p>From large-scale irrigation to localized drinking water projects, our approach is comprehensive.</p>
            </div>
            <div className="waA-grid">
              {initiatives.map((initiative, idx) => (
                <div className="waA-card wa-animate" key={idx}>
                  <div className="waA-icon">{(initiative.match(/\b(\w)/g)||[]).slice(0,2).join('').toUpperCase()}</div>
                  <div>
                    <h5>{initiative}</h5>
                    <p>Learn More →</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
 
          <div className="waA-impact wa-animate" role="region" aria-label="Impact highlights">
            <div className="waA-kpi"><div className="num" data-target="1987">0</div><div>1987 – Water Initiatives Started</div></div>
            <div className="waA-kpi"><div className="num" data-target="1200">0</div><div>Rural Communities Served</div></div>
            <div className="waA-kpi"><div className="num" data-target="6">0</div><div>Water Management Solutions</div></div>
            <div className="waA-kpi"><div className="num" data-target="300">0</div><div>Clean Water Projects Implemented</div></div>
          </div>
 
          <div className="waA-cta wa-animate" role="region" aria-label="Call to action: Support Clean Water Access">
            <h2>Support Clean Water Access</h2>
            <p>Your contribution helps us build vital water infrastructure and ensure safe drinking water for families.</p>
            <div className="d-flex justify-content-center gap-3" style={{flexWrap:'wrap'}}>
              <Button className="btn-donate" onClick={openDonation}>Donate</Button>
              <Button className="btn-join" onClick={() => navigate('/interns')}>Join Us</Button>
            </div>
          </div>
        </div>
      </section>
 
      {/* Call To Action Section (Reusable) */}
      <section className="py-5 text-dark" style={{ backgroundColor: "#ffffffff" }}>
        <Container className="text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <h2 className="display-5 fw-bold mb-3">Support Clean Water Access</h2>
            <p className="lead mb-4">
              Your contribution helps us build vital water infrastructure and ensure safe drinking water for families.
            </p>
            <Button variant="dark" size="lg" className="me-3 fw-bold" onClick={openDonation}>
              Donate
            </Button>
            <Button variant="outline-dark" size="lg" className="fw-bold" onClick={() => navigate("/interns")}>
              Join Us
            </Button>
          </motion.div>
        </Container>
      </section>
 
      <Footer />
 
      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};
 
export default Water;
 